import React, { useEffect, useMemo, useState } from 'react'
import { BASE_URL_Upload } from '../Component/commonComponents/ApiEndpoints'
import { useSidebar } from '../Component/SidebarContext'

const REGISTRY_TITLE = 'Character Certificate'

const formatDdMmYyyy = (value) => {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'string' && /^\d{1,2}-\d{1,2}-\d{4}$/.test(value.trim())) {
    return value.trim()
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) {
    return typeof value === 'string' ? value : ''
  }
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  return `${day}-${month}-${year}`
}

const safeStr = (v, fallback = '') => {
  if (v === null || v === undefined) return fallback
  const s = String(v).trim()
  if (!s || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined') return fallback
  return s
}

const resolveMediaUrl = (src) => {
  const t = safeStr(src, '')
  if (!t) return ''
  if (/^https?:\/\//i.test(t)) return t
  if (t.startsWith('//')) return `https:${t}`
  const base = String(BASE_URL_Upload || '').replace(/\/$/, '')
  const path = t.startsWith('/') ? t : `/${t}`
  return base ? `${base}${path}` : t
}

/** Map API to one of: excellent | good | satisfactory */
const resolveConductLevel = (certificate) => {
  const raw =
    certificate?.conduct_rating ??
    certificate?.character_rating ??
    certificate?.conduct ??
    certificate?.character_remark
  if (raw === null || raw === undefined || raw === '') return 'satisfactory'
  const s = String(raw).toLowerCase()
  if (s.includes('excellent') || s === 'a' || s === '1') return 'excellent'
  if (s.includes('good') || s === 'b' || s === '2') return 'good'
  if (s.includes('satisfactory') || s.includes('fair') || s === 'c' || s === '3') {
    return 'satisfactory'
  }
  return 'satisfactory'
}

const CertificateTemplate3 = ({
  studentName = 'Student Name',
  courseName = 'Course Name',
  date = '',
  instituteName = 'Institute Name',
  certificateTitle = 'Character Certificate',
  authorityName = 'Authorized Signatory',
  certificateId = '',
  logoUrl = '',
  student: studentProp,
  course: courseProp,
  certificate: certificateProp,
  institute: instituteProp,
  selectedStudent,
  selectedCourse,
  suppressSidebarOffset = false,
}) => {
  const certificate = certificateProp || {}
  const student = studentProp || selectedStudent || {}
  const course = courseProp || selectedCourse || {}
  const institute = instituteProp || {}

  const { isCollapsed } = useSidebar()
  const [marginLeft, setMarginLeft] = useState('0px')
  const [photoFailed, setPhotoFailed] = useState(false)
  const [fallbackPhotoFailed, setFallbackPhotoFailed] = useState(false)

  useEffect(() => {
    if (suppressSidebarOffset) return undefined

    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed, suppressSidebarOffset])

  useEffect(() => {
    setPhotoFailed(false)
    setFallbackPhotoFailed(false)
  }, [student?.student_image, student?.photo, student?.image])

  useEffect(() => {
    const root = document.documentElement
    const onBeforePrint = () => root.classList.add('certificate-template-3-print-session')
    const onAfterPrint = () => root.classList.remove('certificate-template-3-print-session')
    window.addEventListener('beforeprint', onBeforePrint)
    window.addEventListener('afterprint', onAfterPrint)
    return () => {
      window.removeEventListener('beforeprint', onBeforePrint)
      window.removeEventListener('afterprint', onAfterPrint)
      root.classList.remove('certificate-template-3-print-session')
    }
  }, [])

  const data = useMemo(() => {
    const instName =
      safeStr(institute.institute_name) ||
      safeStr(institute.name) ||
      safeStr(instituteName)

    const studName = safeStr(student.name) || safeStr(studentName)
    const father = safeStr(student.father_name)
    const mother = safeStr(student.mother_name)
    const admission = safeStr(student.admission_number)

    const crsName =
      safeStr(course.course_name) || safeStr(course.name) || safeStr(courseName)
    const durationRaw = course.course_duration ?? course.duration
    const duration = (() => {
      if (durationRaw === null || durationRaw === undefined) return ''
      const s = String(durationRaw).trim()
      if (!s) return ''
      return /month/i.test(s) ? s : `${s} months`
    })()

    const start = formatDdMmYyyy(course.start_date ?? course.startDate)
    const end = formatDdMmYyyy(course.end_date ?? course.endDate)

    const certNum =
      safeStr(certificate.certificate_number) ||
      safeStr(certificate.certificate_id) ||
      safeStr(certificate.id != null ? String(certificate.id) : '') ||
      safeStr(certificateId)

    const issueRaw =
      certificate.issue_date ?? certificate.date ?? certificate.issueDate ?? date
    const issue = formatDdMmYyyy(issueRaw) || (typeof issueRaw === 'string' ? issueRaw : '')

    const imgRaw =
      student.student_image ?? student.photo ?? student.image ?? student.profile_image
    const photoSrc = resolveMediaUrl(imgRaw)

    const conductLevel = resolveConductLevel(certificate)

    const displayHeading =
      safeStr(certificateTitle) && safeStr(certificateTitle) !== REGISTRY_TITLE
        ? safeStr(certificateTitle).toUpperCase()
        : 'CHARACTER CERTIFICATE'

    return {
      instName,
      studName,
      father,
      mother,
      admission,
      crsName,
      duration,
      start,
      end,
      certNum,
      issue,
      photoSrc,
      conductLevel,
      displayHeading,
    }
  }, [
    certificate,
    student,
    course,
    institute,
    studentName,
    courseName,
    instituteName,
    certificateId,
    certificateTitle,
    date,
  ])

  const {
    instName,
    studName,
    father,
    mother,
    admission,
    crsName,
    duration,
    start,
    end,
    certNum,
    issue,
    photoSrc,
    conductLevel,
    displayHeading,
  } = data

  const qrData = useMemo(() => {
    const parts = [instName, certNum || 'PREVIEW', issue || formatDdMmYyyy(new Date())].filter(
      Boolean,
    )
    return parts.join('|')
  }, [instName, certNum, issue])

  const qrSrc =
    typeof window !== 'undefined'
      ? `https://api.qrserver.com/v1/create-qr-code/?size=64x64&margin=0&data=${encodeURIComponent(
          qrData,
        )}`
      : ''

  const defaultPhoto = `${process.env.PUBLIC_URL || ''}/studentprofile.jpg`
  const showPhoto = photoSrc && !photoFailed
  const logoResolved = resolveMediaUrl(logoUrl) || logoUrl

  const layoutMarginLeft = suppressSidebarOffset ? '0px' : marginLeft

  const handlePrint = () => {
    window.print()
  }

  const parentsLine =
    father && mother
      ? `S/o ${father} and M/o ${mother}`
      : father
        ? `S/o ${father}`
        : mother
          ? `M/o ${mother}`
          : ''

  return (
    <div
      className="certificate-template-3-page"
      style={{
        marginLeft: layoutMarginLeft,
        transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        willChange: 'margin-left',
        WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,600;0,700;1,500&family=Source+Sans+3:wght@400;500;600;700&display=swap');

          .certificate-template-3-page {
            min-height: 100vh;
            background: #e8eaed;
            display: flex;
            align-items: flex-start;
            justify-content: center;
            padding: 24px 16px 40px;
            box-sizing: border-box;
          }

          .certificate-template-3-wrap {
            width: min(210mm, 100%);
          }

          .certificate-template-3-print-stage {
            display: block;
            width: 100%;
          }

          .certificate-template-3-actions {
            display: flex;
            justify-content: flex-end;
            margin-bottom: 12px;
          }

          .certificate-template-3-print-btn {
            border: 1px solid #1e3a5f;
            background: #fff;
            color: #1e3a5f;
            padding: 8px 16px;
            border-radius: 6px;
            font-weight: 600;
            font-family: 'Source Sans 3', system-ui, sans-serif;
            font-size: 13px;
            cursor: pointer;
          }

          .certificate-template-3-sheet {
            --ink: #1a1a1a;
            --muted: #4b5563;
            --border: #94a3b8;
            --accent: #1e3a5f;
            width: 100%;
            aspect-ratio: 210 / 297;
            max-height: min(92vh, 297mm);
            background: #fff;
            position: relative;
            box-shadow: 0 4px 24px rgba(15, 23, 42, 0.08);
            border: 1px solid #cbd5e1;
          }

          .certificate-template-3-frame {
            position: absolute;
            inset: 14px;
            border: 1px solid var(--border);
            pointer-events: none;
          }

          .certificate-template-3-inner {
            position: relative;
            z-index: 2;
            padding: clamp(20px, 4vw, 36px) clamp(18px, 3.5vw, 32px) 28px;
            height: 100%;
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            min-height: 0;
            overflow-y: auto;
          }

          .certificate-template-3-watermark {
            position: absolute;
            inset: 0;
            z-index: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: 'Cormorant Garamond', serif;
            font-size: clamp(56px, 18vw, 140px);
            font-weight: 600;
            color: rgba(30, 58, 95, 0.05);
            letter-spacing: 0.2em;
            text-transform: uppercase;
            pointer-events: none;
            user-select: none;
          }

          .certificate-template-3-head-row {
            display: flex;
            justify-content: center;
            align-items: flex-start;
            gap: 16px;
            margin-bottom: 0;
            position: relative;
            min-height: 72px;
            padding-right: 72px;
            box-sizing: border-box;
            flex-shrink: 0;
            width: 100%;
          }

          .certificate-template-3-head-row .certificate-template-3-logo,
          .certificate-template-3-head-row .certificate-template-3-logo-ph {
            width: 56px;
            height: 56px;
            margin: 0 auto 8px;
            display: block;
          }

          .certificate-template-3-logo {
            border-radius: 4px;
            border: 1px solid var(--border);
            object-fit: cover;
            background: #fff;
          }

          .certificate-template-3-logo-ph {
            border-radius: 4px;
            border: 1px dashed var(--border);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 9px;
            color: #94a3b8;
            text-align: center;
            padding: 4px;
            font-family: 'Source Sans 3', sans-serif;
          }

          .certificate-template-3-head-center {
            flex: 1;
            text-align: center;
            min-width: 0;
          }

          .certificate-template-3-photo {
            position: absolute;
            top: 0;
            right: 0;
            width: 72px;
            height: 88px;
            border: 1px solid var(--border);
            background: #f8fafc;
            overflow: hidden;
            flex-shrink: 0;
          }

          .certificate-template-3-photo img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .certificate-template-3-photo-ph {
            font-size: 9px;
            color: #94a3b8;
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100%;
            padding: 4px;
            text-align: center;
            font-family: 'Source Sans 3', sans-serif;
          }

          .certificate-template-3-institute {
            margin: 0;
            font-family: 'Source Sans 3', sans-serif;
            font-size: clamp(14px, 2vw, 17px);
            font-weight: 600;
            letter-spacing: 0.06em;
            text-transform: uppercase;
            color: var(--accent);
          }

          .certificate-template-3-title {
            margin: 12px auto 0;
            font-family: 'Cormorant Garamond', serif;
            font-size: clamp(22px, 3.2vw, 28px);
            font-weight: 700;
            letter-spacing: 0.14em;
            color: var(--ink);
            text-transform: uppercase;
            display: inline-block;
            max-width: 100%;
            padding-bottom: 10px;
            border-bottom: 2px solid var(--accent);
            box-sizing: border-box;
            line-height: 1.25;
          }

          .certificate-template-3-body {
            flex: 1 0 auto;
            text-align: center;
            font-family: 'Source Sans 3', system-ui, sans-serif;
            font-size: clamp(12px, 1.35vw, 14px);
            line-height: 1.75;
            color: var(--muted);
            margin-top: 20px;
            padding-top: 4px;
            position: relative;
            z-index: 1;
            flex-shrink: 0;
            min-height: min-content;
          }

          .certificate-template-3-name {
            font-weight: 700;
            color: var(--ink);
            font-size: clamp(15px, 1.6vw, 17px);
          }

          .certificate-template-3-remarks {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 16px 24px;
            margin: 18px 0 8px;
            font-size: 12px;
            color: var(--ink);
            font-family: 'Source Sans 3', sans-serif;
          }

          .certificate-template-3-remark {
            display: inline-flex;
            align-items: center;
            gap: 6px;
          }

          .certificate-template-3-remark input {
            accent-color: var(--accent);
            width: 14px;
            height: 14px;
          }

          .certificate-template-3-details {
            margin-top: 16px;
            padding: 12px 14px;
            border: 1px solid #e2e8f0;
            border-radius: 4px;
            background: #fafbfc;
            text-align: left;
            max-width: 420px;
            margin-left: auto;
            margin-right: auto;
            font-size: 12px;
            color: var(--muted);
            font-family: 'Source Sans 3', sans-serif;
          }

          .certificate-template-3-details-row {
            display: flex;
            justify-content: space-between;
            gap: 12px;
            padding: 4px 0;
            border-bottom: 1px solid #e5e7eb;
          }

          .certificate-template-3-details-row:last-child {
            border-bottom: none;
          }

          .certificate-template-3-details dt {
            font-weight: 600;
            color: #374151;
            margin: 0;
          }

          .certificate-template-3-details dd {
            margin: 0;
            text-align: right;
            word-break: break-word;
          }

          .certificate-template-3-meta {
            margin-top: 14px;
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 20px 32px;
            font-size: 11px;
            color: var(--muted);
            font-family: 'Source Sans 3', sans-serif;
          }

          .certificate-template-3-meta strong {
            color: var(--ink);
            font-weight: 600;
          }

          .certificate-template-3-bottom {
            margin-top: auto;
            padding-top: 16px;
            display: flex;
            flex-wrap: wrap;
            align-items: flex-end;
            justify-content: space-between;
            gap: 16px;
            flex-shrink: 0;
            width: 100%;
          }

          .certificate-template-3-qr {
            text-align: center;
          }

          .certificate-template-3-qr img {
            display: block;
            width: 56px;
            height: 56px;
          }

          .certificate-template-3-qr-cap {
            font-size: 8px;
            color: #64748b;
            margin-top: 2px;
          }

          .certificate-template-3-signatures {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 12px;
            flex: 1;
            min-width: 0;
          }

          .certificate-template-3-sign {
            text-align: center;
            font-family: 'Source Sans 3', sans-serif;
          }

          .certificate-template-3-seal {
            width: 44px;
            height: 44px;
            margin: 0 auto 6px;
            border: 2px solid #64748b;
            border-radius: 50%;
            font-size: 7px;
            display: flex;
            align-items: center;
            justify-content: center;
            text-transform: uppercase;
            color: #64748b;
            background: #f8fafc;
          }

          .certificate-template-3-sign-line {
            border-top: 1px solid var(--ink);
            margin-top: 28px;
            padding-top: 6px;
            font-size: 10px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.06em;
            color: var(--ink);
          }

          .certificate-template-3-sign-sub {
            font-size: 9px;
            color: var(--muted);
            margin-top: 2px;
            font-weight: 500;
            text-transform: none;
            letter-spacing: 0;
          }

          @media (max-width: 520px) {
            .certificate-template-3-head-row {
              flex-wrap: wrap;
              padding-right: 0;
            }

            .certificate-template-3-photo {
              position: relative;
              margin: 0 auto 12px;
            }

            .certificate-template-3-signatures {
              grid-template-columns: 1fr;
            }
          }

          @media print {
            /*
             * TemplateBasedCertificate: same approach as CertificateTemplate4/6 — do not use
             * body visibility hacks or position:absolute; they break embed layout and print sizing.
             */
            @page {
              size: A4 portrait;
              // margin: 5mm;
            }

            html,
            body,
            #root {
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
              max-width: none !important;
              background: #fff !important;
              height: auto !important;
              min-height: 0 !important;
              max-height: none !important;
              overflow: visible !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }

            .App:has(.certificate-template-3-page) > *:not(:last-child),
            html.certificate-template-3-print-session .App > *:not(:last-child) {
              display: none !important;
            }

            .App:has(.certificate-template-3-page) > *:last-child,
            html.certificate-template-3-print-session .App > *:last-child {
              width: 100% !important;
              max-width: 100% !important;
              flex: 1 1 auto !important;
              min-width: 0 !important;
              padding: 0 !important;
              margin: 0 !important;
            }

            .template-based-certificate-root {
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
              max-width: none !important;
              min-width: 0 !important;
              box-sizing: border-box !important;
            }

            .template-tc-print-preview-wrap {
              padding: 0 !important;
              margin: 0 !important;
              width: 100% !important;
              max-width: none !important;
              box-sizing: border-box !important;
              break-inside: avoid;
              page-break-inside: avoid;
            }

            .template-preview-shell,
            .template-preview-surface,
            .template-preview-surface > div {
              overflow: visible !important;
              max-height: none !important;
              max-width: none !important;
              width: 100% !important;
              margin-left: 0 !important;
              margin-right: 0 !important;
              height: auto !important;
              break-inside: avoid;
              page-break-inside: avoid;
            }

            .certificate-template-3-page {
              position: relative !important;
              left: auto !important;
              top: auto !important;
              min-height: 0 !important;
              height: auto !important;
              width: 100% !important;
              max-width: none !important;
              padding: 0 !important;
              margin: 0 !important;
              margin-left: 0 !important;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: flex-start;
              background: #fff !important;
              box-sizing: border-box;
              overflow: visible !important;
              break-inside: avoid;
              page-break-inside: avoid;
              page-break-after: avoid;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            .certificate-template-3-page,
            .certificate-template-3-page * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            .certificate-template-3-wrap {
              width: 100%;
              max-width: none;
              margin: 0;
              flex-shrink: 0;
              break-inside: avoid;
              page-break-inside: avoid;
            }

            .certificate-template-3-actions,
            .certificate-template-3-actions * {
              display: none !important;
            }

            /*
             * After 5mm @page: content box 200×277 mm. Keep 210:297 with
             * width = 277 * 210/297, height = 277; centered in stage.
             */
            .certificate-template-3-print-stage {
              position: relative !important;
              display: flex !important;
              flex-direction: row !important;
              align-items: center !important;
              justify-content: center !important;
              width: 100% !important;
              min-width: 0 !important;
              max-width: 200mm !important;
              height: 277mm !important;
              min-height: 277mm !important;
              max-height: 277mm !important;
              margin: 0 auto !important;
              padding: 0 !important;
              box-sizing: border-box !important;
              overflow: hidden !important;
              transform: none !important;
              break-inside: avoid !important;
              page-break-inside: avoid !important;
            }

            .certificate-template-3-sheet {
              position: relative !important;
              width: calc(277mm * 210 / 297) !important;
              max-width: 100% !important;
              height: 277mm !important;
              min-height: 0 !important;
              max-height: 277mm !important;
              aspect-ratio: unset !important;
              margin: 0 !important;
              background: #fff !important;
              box-shadow: none !important;
              border-radius: 0 !important;
              display: flex !important;
              flex-direction: column !important;
              flex: 0 0 auto !important;
              break-inside: avoid !important;
              page-break-inside: avoid !important;
              overflow: hidden !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            .certificate-template-3-frame {
              display: none !important;
            }

            .certificate-template-3-inner {
              flex: 1 1 auto !important;
              min-height: 0 !important;
              height: 100% !important;
              display: flex !important;
              flex-direction: column !important;
              justify-content: space-between !important;
              padding: 10px 14px 12px !important;
              box-sizing: border-box !important;
              overflow: hidden !important;
            }

            .certificate-template-3-watermark {
              font-size: 72px !important;
            }

            .certificate-template-3-head-row {
              min-height: 0 !important;
              padding-right: 68px !important;
              flex-shrink: 0 !important;
            }

            .certificate-template-3-head-center .certificate-template-3-logo,
            .certificate-template-3-head-center .certificate-template-3-logo-ph {
              width: 48px !important;
              height: 48px !important;
              margin-bottom: 6px !important;
            }

            .certificate-template-3-logo {
              object-fit: contain !important;
            }

            .certificate-template-3-institute {
              font-size: 16px !important;
            }

            .certificate-template-3-title {
              font-size: 24px !important;
              margin-top: 8px !important;
              padding-bottom: 8px !important;
            }

            .certificate-template-3-photo {
              width: 64px !important;
              height: 78px !important;
            }

            .certificate-template-3-body {
              flex: 0 1 auto !important;
              min-height: 0 !important;
              text-align: center;
              font-size: 15px !important;
              line-height: 1.6 !important;
              margin-top: 12px !important;
            }

            .certificate-template-3-name {
              font-size: 17.5px !important;
            }

            .certificate-template-3-remarks {
              font-size: 14px !important;
              margin: 14px 0 6px !important;
              gap: 12px 20px !important;
            }

            .certificate-template-3-details {
              max-width: 100% !important;
              width: 100% !important;
              box-sizing: border-box !important;
              margin-top: 12px !important;
              padding: 10px 12px !important;
              font-size: 14px !important;
            }

            .certificate-template-3-details-row {
              padding: 6px 0 !important;
            }

            .certificate-template-3-meta {
              margin-top: 12px !important;
              font-size: 12.5px !important;
              gap: 12px 24px !important;
            }

            .certificate-template-3-bottom {
              margin-top: 0 !important;
              padding-top: 8px !important;
              width: 100% !important;
              align-items: flex-end !important;
              flex-shrink: 0 !important;
            }

            .certificate-template-3-qr img {
              width: 50px !important;
              height: 50px !important;
            }

            .certificate-template-3-qr-cap {
              font-size: 9px !important;
            }

            .certificate-template-3-signatures {
              flex: 1 1 auto !important;
              min-width: 0 !important;
              gap: 8px !important;
            }

            .certificate-template-3-sign-line {
              margin-top: 18px !important;
              font-size: 10.5px !important;
            }

            .certificate-template-3-sign-sub {
              font-size: 9.5px !important;
            }

            .certificate-template-3-seal {
              width: 40px !important;
              height: 40px !important;
              font-size: 7.5px !important;
            }

            .certificate-template-3-print-stage,
            .certificate-template-3-sheet {
              page-break-before: avoid !important;
              break-before: avoid-page !important;
              page-break-after: avoid !important;
              break-after: avoid-page !important;
            }
          }
        `}
      </style>

      <div className="certificate-template-3-wrap">
        <div className="certificate-template-3-actions">
          <button type="button" className="certificate-template-3-print-btn" onClick={handlePrint}>
            Print / Download
          </button>
        </div>

        <div className="certificate-template-3-print-stage">
        <article className="certificate-template-3-sheet">
          <div className="certificate-template-3-frame" aria-hidden />
          <div className="certificate-template-3-watermark" aria-hidden>
            Official
          </div>

          <div className="certificate-template-3-inner">
            <header className="certificate-template-3-head-row">
              <div className="certificate-template-3-head-center">
                {logoResolved ? (
                  <img src={logoResolved} alt="" className="certificate-template-3-logo" />
                ) : (
                  <div className="certificate-template-3-logo-ph">Logo</div>
                )}
                <p className="certificate-template-3-institute">{instName || 'Institute Name'}</p>
                <h1 className="certificate-template-3-title">{displayHeading}</h1>
              </div>

              <div className="certificate-template-3-photo" aria-label="Student photograph">
                {showPhoto ? (
                  <img src={photoSrc} alt="" onError={() => setPhotoFailed(true)} />
                ) : !fallbackPhotoFailed ? (
                  <img
                    src={defaultPhoto}
                    alt=""
                    onError={() => setFallbackPhotoFailed(true)}
                  />
                ) : (
                  <div className="certificate-template-3-photo-ph">Photo</div>
                )}
              </div>
            </header>

            <section className="certificate-template-3-body">
              <p style={{ margin: '0 0 12px' }}>
                This is to certify that{' '}
                <span className="certificate-template-3-name">{studName || '—'}</span>
                {parentsLine ? (
                  <>
                    ,<br />
                    {parentsLine},
                  </>
                ) : admission ? (
                  ','
                ) : null}
                {admission ? (
                  <>
                    <br />
                    bearing Admission No. <strong style={{ color: '#1a1a1a' }}>{admission}</strong>,
                  </>
                ) : null}
                <br />
                was a student of our institution.
              </p>

              <p style={{ margin: '0 0 12px' }}>
                During their period of study in the course{' '}
                <strong style={{ color: '#1a1a1a' }}>{crsName || '—'}</strong>
                {start || end ? (
                  <>
                    , from <strong style={{ color: '#1a1a1a' }}>{start || '—'}</strong> to{' '}
                    <strong style={{ color: '#1a1a1a' }}>{end || '—'}</strong>
                  </>
                ) : null}
                , their conduct and character were found to be good and satisfactory.
              </p>

              <p style={{ margin: 0 }}>We wish them success in their future endeavors.</p>

              <div className="certificate-template-3-remarks" aria-label="Conduct remarks">
                <label className="certificate-template-3-remark">
                  <input type="checkbox" readOnly checked={conductLevel === 'excellent'} />
                  Excellent
                </label>
                <label className="certificate-template-3-remark">
                  <input type="checkbox" readOnly checked={conductLevel === 'good'} />
                  Good
                </label>
                <label className="certificate-template-3-remark">
                  <input type="checkbox" readOnly checked={conductLevel === 'satisfactory'} />
                  Satisfactory
                </label>
              </div>

              <dl className="certificate-template-3-details">
                <div className="certificate-template-3-details-row">
                  <dt>Course</dt>
                  <dd>{crsName || '—'}</dd>
                </div>
                <div className="certificate-template-3-details-row">
                  <dt>Duration</dt>
                  <dd>{duration || '—'}</dd>
                </div>
                <div className="certificate-template-3-details-row">
                  <dt>Admission No.</dt>
                  <dd>{admission || '—'}</dd>
                </div>
              </dl>

              <div className="certificate-template-3-meta">
                <span>
                  <strong>Certificate No:</strong> {certNum || '—'}
                </span>
                <span>
                  <strong>Issue Date:</strong> {issue || '—'}
                </span>
              </div>
            </section>

            <footer className="certificate-template-3-bottom">
              <div className="certificate-template-3-qr">
                <img src={qrSrc} alt="" width={56} height={56} />
                <div className="certificate-template-3-qr-cap">Verification</div>
              </div>

              <div className="certificate-template-3-signatures">
                <div className="certificate-template-3-sign">
                  <div className="certificate-template-3-sign-line">{authorityName}</div>
                  <div className="certificate-template-3-sign-sub">Authorized Signatory</div>
                </div>
                <div className="certificate-template-3-sign">
                  <div className="certificate-template-3-seal">Seal</div>
                  <div className="certificate-template-3-sign-line">Official Seal</div>
                  <div className="certificate-template-3-sign-sub">Optional</div>
                </div>
                <div className="certificate-template-3-sign">
                  <div className="certificate-template-3-sign-line">Principal / Head</div>
                  <div className="certificate-template-3-sign-sub">Head of Institution</div>
                </div>
              </div>
            </footer>
          </div>
        </article>
        </div>
      </div>
    </div>
  )
}

export default CertificateTemplate3
